import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, Play, Pause, Download, Loader } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { whatsappMediaService } from '@/services/whatsappMediaService';
import { FormattedMessageContent } from '@/components/chat/FormattedMessageContent';
import type { Message } from '@/types/message-types';

interface MediaAudioProps {
  message: Message;
  tenantId: string;
}

export function MediaAudio({ message, tenantId }: MediaAudioProps) {
  const { t } = useTranslation('conversations');
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null);

  useEffect(() => {
    const loadAudio = async () => {
      // Debug logging for customer media issue
      console.log('🎵 MediaAudio loadAudio:', {
        messageId: message.id,
        senderType: message.sender_type,
        hasMediaUrl: !!message.media_url,
        mediaUrlValue: message.media_url,
        mediaUrlLength: message.media_url?.length,
        metadata: message.metadata
      });

      // First check if media_url is already available — resolve storage paths to signed URLs
      if (message.media_url?.trim()) {
        const resolved = await whatsappMediaService.resolveMediaUrl(message.media_url);
        if (resolved) {
          console.log('✅ MediaAudio: Using resolved media_url');
          setAudioUrl(resolved);
          setLoading(false);
          return;
        }
      }

      // Fallback to extracting media ID and downloading
      console.log('📥 MediaAudio: Attempting to extract media_id from metadata');
      const mediaId = whatsappMediaService.extractMediaIdFromMessage(message);
      console.log('🔍 MediaAudio: Extracted media_id:', mediaId);

      if (!mediaId) {
        console.error('❌ MediaAudio: No media ID found in message:', {
          messageId: message.id,
          metadata: message.metadata,
          media_url: message.media_url
        });
        setError('No media ID found');
        setLoading(false);
        return;
      }

      try {
        const result = await whatsappMediaService.downloadAndGetMediaUrl(
          tenantId,
          mediaId,
          'audio',
          message.id
        );

        if (result.success) {
          setAudioUrl(result.fileUrl || null);
        } else {
          setError(result.error || 'Failed to load audio');
        }
      } catch (err) {
        setError('Failed to load audio');
      } finally {
        setLoading(false);
      }
    };

    loadAudio();
  }, [message, tenantId]);

  useEffect(() => {
    if (audioUrl && !audioRef) {
      const audio = new Audio(audioUrl);
      audio.addEventListener('ended', () => setIsPlaying(false));
      setAudioRef(audio);
    }
    
    return () => {
      if (audioRef) {
        audioRef.pause();
        audioRef.removeEventListener('ended', () => setIsPlaying(false));
      }
    };
  }, [audioUrl]);

  const handlePlayPause = () => {
    if (!audioRef) return;

    if (isPlaying) {
      audioRef.pause();
      setIsPlaying(false);
    } else {
      audioRef.play();
      setIsPlaying(true);
    }
  };

  const handleDownload = () => {
    if (audioUrl) {
      const link = document.createElement('a');
      link.href = audioUrl;
      link.download = `audio_${message.id}.mp3`;
      link.click();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
        <Loader className="h-5 w-5 animate-spin" />
        <span className="text-sm">{t('media.loadingAudio')}</span>
      </div>
    );
  }

  if (error || !audioUrl) {
    return (
      <div className="flex items-center gap-2">
        <Mic className="h-4 w-4" />
        <span className="text-sm">🎵 Audio message</span>
        {error && <span className="text-xs text-muted-foreground">({error})</span>}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg max-w-sm">
      <Button
        size="sm"
        variant="ghost"
        onClick={handlePlayPause}
        className="h-10 w-10 p-0 rounded-full"
      >
        {isPlaying ? (
          <Pause className="h-5 w-5" />
        ) : (
          <Play className="h-5 w-5" />
        )}
      </Button>
      
      <div className="flex-1">
        <div className="text-sm font-medium">{t('media.voiceMessage')}</div>
        <div className="text-xs text-muted-foreground">
          {message.content && <FormattedMessageContent content={message.content} />}
        </div>
      </div>
      
      <Button
        size="sm"
        variant="ghost"
        onClick={handleDownload}
        className="h-8 w-8 p-0"
      >
        <Download className="h-4 w-4" />
      </Button>
    </div>
  );
}